import React, { useCallback, useEffect, useMemo } from 'react';
import LanguageBarItem from 'components/LanguageBarItem/LanguageBarItem';
import { TLanguageIDs, IMenuTranslation, ICustomerLanguages } from 'types';
import { trueOrderLanguageIDs } from 'appConstants';
import style from './LanguageBar.module.scss';

interface ILanguageBar {
	selectedTranslationTab: TLanguageIDs | null;
	languageIDs: TLanguageIDs[];
	translations?: IMenuTranslation[];
	defaultLanguage?: ICustomerLanguages;
	setTranslationTab: (value: TLanguageIDs | null) => void;
}

const LanguageBar: React.FC<ILanguageBar> = ({
	selectedTranslationTab,
	languageIDs,
	translations,
	defaultLanguage,
	setTranslationTab,
}) => {
	const getTrueLanguagesOrder = useCallback(() => {
		const languages = [...languageIDs];
		const sortByObject: { [key: string]: number } = trueOrderLanguageIDs.reduce((obj, item, index) => {
			return {
				...obj,
				[item]: index,
			};
		}, {});
		languages.sort((a, b) => sortByObject[a] - sortByObject[b]);
		return languages;
	}, [languageIDs]);

	const sortedLanguages = useMemo<TLanguageIDs[]>(() => getTrueLanguagesOrder(), [getTrueLanguagesOrder]);

	const languageBarItemsList = useMemo(() => {
		return sortedLanguages.map((el) => {
			const itemHasError =
				translations?.find(
					(translation) => translation.languageId === el && translation.languageId === defaultLanguage?.id,
				)?.name === '';

			return (
				<LanguageBarItem
					key={el}
					languageId={el}
					isActive={selectedTranslationTab === el}
					isDefault={defaultLanguage?.id === el}
					itemHasError={itemHasError}
					onClick={() => setTranslationTab(el)}
				/>
			);
		});
	}, [sortedLanguages, selectedTranslationTab, defaultLanguage, setTranslationTab]);

	useEffect(() => {
		const defaultValue = sortedLanguages.length ? sortedLanguages[0] : null;
		setTranslationTab(defaultValue);
	}, [sortedLanguages.length]);

	return (
		<>
			{languageIDs.length ? (
				<div className={style.container} data-test="LanguageBar">
					{languageBarItemsList}
				</div>
			) : null}
		</>
	);
};

export default React.memo(LanguageBar);
